import React, { useCallback, useState, useEffect, useReducer } from 'react';
import { Link } from 'react-router-dom';
import SettingsEditor from '../SettingsEditor';
import VideoStage from '../VideoStage';
import MotionTimeline from '../MotionTimeline';
import UpgradePromptDialog from '../UpgradePromptDialog';
import SpeedDialOverlay from '../SpeedDialOverlay';
import OperationCards from './OperationCards';
import { UPSELL_PROMPTS, resolveUnlockTier } from '../../upsell/triggers';
import { persistFootageRotation } from '../../videoUpload/footageRotation';
import { isConcatInFlight } from '../../utils/footageConcat';
import {
    renderBlockReason,
    clipCount,
    deriveEditDraft,
    compileStaleDeadlineMs,
    isRenderInFlight,
    buildMotionSettings,
    motionModeOf,
    MOTION_MODE_PACE,
} from '../../lib/editDraft';
import { SPEED_CURVE_FIELD } from '../../lib/motionPace';
import useFeatureFlag from '../../hooks/useFeatureFlag';
import {
    featureFlagOf,
    featureFlagDefaultOf,
    VARIABLE_SPEED,
} from '../../lib/operations';
import { MOTION_PACE_CHART, MOTION_SECTION, OPERATIONS } from '../../lib/operationCopy';

// Why the render button is unavailable, said in a way that names the way out —
// when there is one. The old copy ("No motion segments detected. Adjust the
// motion threshold and recalculate…") was a dead end: on a low-motion project
// no threshold produces segments, and nothing on the page offered the
// alternative (#401).
//
// `analysis_pending` and `compile_pending` deliberately name NO escape.
// generate_edited_video refuses a render for the whole analysis window AND for
// the whole photo-compile window (both SOURCE_NOT_READY), so "switch off Trim
// stillness to render now" would persist the flip and then fail on the generic
// error alert.
// The two segment-shaped blockers name the control the user has to switch off,
// and that control is renamed by the `motionPace` flag (#590): dark it is the
// standalone "Trim stillness" card, lit it is the "Motion" section holding the
// MotionTrim/MotionPace selector. Pointing at a name that is not on screen is
// worse than not pointing at all, so the string follows the flag. Dark, both
// sentences are byte-identical to pre-#532.
const blockCopy = (motionPaceEnabled) => {
  const control = motionPaceEnabled ? MOTION_SECTION.displayName : OPERATIONS.trim_stillness.displayName;
  return {
    no_source: 'Waiting for your footage to finish uploading.',
    analysis_pending:
      'Motion analysis is still running. Set up the rest of your edit now — you can render once analysis finishes.',
    compile_pending:
      'Your photos are still being compiled into a video. Set up the rest of your edit now — you can render once the video is ready.',
    analysis_unavailable:
      `Motion analysis didn't finish for this footage, so there are no segments to trim. Switch off ${control} above to render the full length instead.`,
    no_segments:
      `No motion was found in this footage, so there is nothing to trim. Switch off ${control} above to render the full length instead.`,
  };
};

const EditorPanel = ({
    currentProject,
    videoUrl,
    videoPlayerRef,
    onSourcePlay,
    onSourceError,
    onRecalculate,
    isRecalculating,
    editedDurationSeconds,
    editedDurationPending = false,
    // The paced render's clip count, or null on a trim render — see `clips`.
    pacedClipCount = null,
    calculateEditedDuration,
    estimateCreditCost,
    credits,
    isCreating,
    onCreateVideo,
    projectId,
    // #350 — effective entitlements, for the credits upsell on the shortfall
    // warning below. Absent, the upsell is simply not offered.
    entitlements,
    // #401 — the operation cards' working state, owned by ProjectView so it
    // survives a tab switch and can be persisted at render time.
    editDraft,
    onEditDraftChange,
    // False while motion analysis is pending/failed. The cards still render —
    // deflicker, transitions and the watermark do not need analysis — but the
    // motion tuning UI and the analysis-derived stats are withheld.
    analysisReady = true,
}) => {
    const [showCreditsUpsell, setShowCreditsUpsell] = useState(false);
    // #532 — MotionPace is dark-launched. Both the flag NAME and its fallback
    // come from the operation registry, so this hides exactly the operation the
    // resolver would refuse rather than a flag that happens to share its name.
    const { enabled: motionPaceEnabled } = useFeatureFlag(
        featureFlagOf(VARIABLE_SPEED),
        { defaultValue: featureFlagDefaultOf(VARIABLE_SPEED) },
    );
    // The same one-field write the Footage tab makes; the project snapshot
    // turns every preview a beat later, so nothing local is held here.
    const handleRotateFootage = useCallback(
        (next) => persistFootageRotation(projectId, next),
        [projectId],
    );
    // Withheld until the users/{uid} snapshot lands, mirroring `watermarkLocked`
    // in the watermark card. Until then a Solo/Pro account is indistinguishable
    // from Free, so an unguarded door would pitch a plan to someone who already
    // pays for a better one, report `from_tier: "free"` on the very dimension
    // this exists to measure, and — because `subscription` is still null — hand
    // an existing subscriber a checkout the server rejects.
    const entitlementsLoading = Boolean(entitlements && entitlements.loading);
    // Only pitch a plan that actually grants MORE monthly credits than this
    // account already gets — a Pro out of credits needs a top-up, not a
    // "plan" with a smaller allotment.
    const canUpgradeForCredits = !entitlementsLoading && Boolean(resolveUnlockTier(UPSELL_PROMPTS.CREDITS_EXHAUSTED, {
        monthlyCredits: (entitlements && entitlements.monthlyCredits) || 0,
    }));

    // Defensive default: a caller that hasn't wired the draft yet still gets
    // today's behaviour. Derived by the SAME helper ProjectView seeds from, so
    // the fallback honours `settings.operations.<id>.enabled` — the enable layer
    // that outranks the legacy tuning keys everywhere else, including in the
    // worker. Hand-rolling it here re-derived a draft that ignored it.
    const draft = editDraft || deriveEditDraft(currentProject.settings);
    const handleDraftChange = onEditDraftChange || (() => {});

    // #532 — the ONE card that has to persist before a render.
    //
    // MotionPace's speed curve is computed server-side by the settings-save
    // callable and read back off `video.speedCurve`; nothing in the browser may
    // derive it (see packages/functions/shared/motion_pace.py). So choosing
    // MotionPace, or moving one of its two sliders, has to write — otherwise
    // there is no curve to chart. It routes through the SAME `onRecalculate`
    // the motion threshold slider uses, and `buildMotionSettings` writes the
    // motion keys and nothing else, so the rest of the draft stays a render-time
    // concern exactly as it is today.
    const settings = currentProject.settings;
    const handleMotionPersist = useCallback(
        (nextDraft) => onRecalculate(buildMotionSettings(settings, nextDraft)),
        [onRecalculate, settings],
    );

    // The client's mirror of the worker's precondition. Unlike the pre-#401 gate
    // this is a REASON, and every reason names a control that is on screen — a
    // zero-segment project is one switch away from rendering rather than stuck.
    // The whole project, not just its video: two of the three server
    // preconditions this mirrors read `photoCompile`.
    const blockReason = renderBlockReason(currentProject, draft);

    // MotionPace's speed-dial preview (#533), drawn over the player so the
    // switch in the Motion section answers its own question without a render.
    //
    // Gated on the RESOLVED mode rather than on `draft.showSpeedDial` alone.
    // `motionModeOf` runs the same resolver the render does — the dark-launch
    // feature flag, then the MotionTrim/MotionPace exclusive group — so a
    // project whose stored recipe asks for both, or one whose flag is off,
    // previews the dial exactly when the worker would burn one in.
    const speedDialVisible = draft.showSpeedDial
        && motionModeOf(draft, motionPaceEnabled) === MOTION_MODE_PACE;
    // The dial takes the corner opposite a bottom-left watermark
    // (speed_dial.build_spec) — but only a mark that will actually render THERE
    // moves it, and that takes both halves of the worker's chain:
    //
    // - `should_watermark` decides IF a mark renders: only an entitled account
    //   that asked to remove it gets a clean frame.
    // - `customization_from_settings` decides WHERE, and it returns `{}` for any
    //   account without `watermarkRemoval` — so an unentitled mark always lands
    //   on the default bottom-right and never displaces the dial.
    //
    // So the CORNER needs the entitlement gate too, not just the switch.
    // `settings.watermarkPosition` survives a downgrade — the API persists it as
    // hygiene, not authorization (`_sanitize_watermark_customization`) — so a
    // project saved while Pro at bottom-left would otherwise preview the dial
    // bottom-right while the render drew it bottom-left: the exact preview /
    // download disagreement this overlay exists to prevent. Same gate, same
    // reason, as the account-defaults blob in ProjectView. While entitlements
    // are still loading this reads as unentitled, which lands on the Free
    // behaviour (mark bottom-right, dial bottom-left) rather than on a stored
    // corner nobody will render.
    const dialWatermarkPosition = entitlements?.watermarkRemoval && !draft.removeWatermark
        ? draft.watermarkPosition
        : null;

    // `compile_pending` is the one block that lifts on a CLOCK rather than on a
    // write: it expires 15 minutes after the last `photoCompile.updatedAt`, so a
    // render is no longer refused by a worker that died mid-compile. Nothing
    // else re-renders this panel on that path — a live worker heartbeats every
    // ~2s, but a SIGKILLed one writes nothing, so no Firestore snapshot arrives
    // to re-evaluate the gate at the moment it flips. Without this timer the
    // button stays greyed out past the deadline until the user reloads, which is
    // exactly the "no in-app recovery" the expiry was added to prevent.
    //
    // One timer, armed only while blocked, firing once. `compile_pending`
    // implies the compile is not YET stale, so the delay is always positive and
    // this cannot spin. Keyed on the deadline (absolute) rather than the
    // remaining time (fresh every render) so a heartbeat does not re-arm it.
    const [, recheckCompileStale] = useReducer((n) => n + 1, 0);
    const staleDeadlineMs = blockReason === 'compile_pending'
        ? compileStaleDeadlineMs(currentProject.photoCompile)
        : null;
    useEffect(() => {
        if (staleDeadlineMs === null) return undefined;
        const timer = setTimeout(recheckCompileStale, Math.max(0, staleDeadlineMs - Date.now()));
        return () => clearTimeout(timer);
    }, [staleDeadlineMs]);

    // The motion tuning UI, handed to the trim card as its inline settings. It
    // only exists inside that card now, so it is impossible to be looking at a
    // motion threshold slider for an operation that is switched off.
    const motionControls = analysisReady ? (
        <div className="op-motion-controls">
            <SettingsEditor
                settings={currentProject.settings}
                calculatedMotionThreshold={currentProject.video?.calculatedMotionThreshold ?? null}
                highestMotionValue={Math.max(0, ...(currentProject.video?.motionValues || []))}
                effectiveMinThreshold={currentProject.video?.effectiveMinThreshold}
                effectiveMaxThreshold={currentProject.video?.effectiveMaxThreshold}
                fps={currentProject.video?.fps}
                videoLengthSeconds={currentProject.video?.length}
                onRecalculate={onRecalculate}
                // So the panel title agrees with the selector above it (#590).
                motionPaceEnabled={motionPaceEnabled}
                motionAlgorithmVersion={currentProject.video?.motionAlgorithmVersion}
                motionValueCount={Array.isArray(currentProject.video?.motionValues) ? currentProject.video.motionValues.length : 0}
                projectId={projectId}
            />
            <div className="visualization-container">
                <MotionTimeline
                    motionValues={currentProject.video?.motionValues || []}
                    motionSegments={currentProject.video?.motionSegments || []}
                    motionThreshold={currentProject.settings?.motionThreshold ?? null}
                    captureGapFrames={currentProject.video?.captureGapFrames}
                    fps={currentProject.video?.fps}
                    videoRef={videoPlayerRef}
                    videoUrl={videoUrl}
                    isRecalculating={isRecalculating}
                    projectId={projectId}
                />
            </div>
        </div>
    ) : null;

    // The whole draft, not just trimStillness: this sits beside Edited Duration,
    // which is already capture-gap-split-aware, and the two must agree.
    //
    // Keeping that promise means answering for the SAME render the duration
    // does. A MotionPace render splits at capture gaps remapped into the paced
    // video's output frames, so a gap that splits in source frames can be too
    // short to fade once the walk has sprinted through it — hence the paced
    // count from `pacedRenderPlan` rather than a local recount of the source
    // segments. And when the length cannot be stated, neither can this: the two
    // cells go quiet together instead of one counting clips off a curve the
    // other just disowned.
    const clips = editedDurationPending
        ? null
        : (pacedClipCount ?? clipCount(currentProject.video, draft));

    return (
        <div>
            {videoUrl && (
                <div className="pv-main-content">
                    <div className="pv-left-column">
                        {/* Same source, same orientation as the Footage tab.
                            Without this the picture flips back the moment the
                            user opens the editor, which reads as the rotation
                            not having saved. The rendered result below is NOT
                            wrapped — FFmpeg has already baked the rotation in,
                            and turning it again would undo it. */}
                        <VideoStage
                            src={videoUrl}
                            ref={videoPlayerRef}
                            rotation={currentProject.footageRotation}
                            width={currentProject.video?.resolution?.width}
                            height={currentProject.video?.resolution?.height}
                            onPlay={onSourcePlay}
                            onError={onSourceError}
                            // Reachable here for the same reason it is on
                            // Overview: this is a picture of the source, and a
                            // user who spots the camera was mounted sideways
                            // should not have to go and find another tab.
                            // Locked by the same combine gate the Footage tab
                            // uses — three surfaces offering the write, one
                            // rule about when it is allowed.
                            onRotate={handleRotateFootage}
                            rotateDisabled={isConcatInFlight(currentProject.videoConcat)}
                            overlay={speedDialVisible ? (
                                <SpeedDialOverlay
                                    videoRef={videoPlayerRef}
                                    speedCurve={currentProject.video?.[SPEED_CURVE_FIELD]}
                                    watermarkPosition={dialWatermarkPosition}
                                />
                            ) : null}
                        />
                    </div>
                </div>
            )}

            <OperationCards
                draft={draft}
                onDraftChange={handleDraftChange}
                video={currentProject.video}
                fps={currentProject.video?.fps}
                entitlements={entitlements}
                projectId={projectId}
                videoUrl={videoUrl}
                motionControls={motionControls}
                motionPaceEnabled={motionPaceEnabled}
                // The same player MotionTimeline and the speed dial already
                // read: the pace chart is a scrubber for it too (#575).
                videoPlayerRef={videoPlayerRef}
                onMotionPersist={handleMotionPersist}
                isRecalculating={isRecalculating}
            />

            {analysisReady && (
                <div className="pv-full-width">
                    <div className="edited-video-stats">
                        <div className="stat-item">
                            <span className="stat-label">Clips</span>
                            <span className="stat-value" data-testid="stat-clips">{clips ?? '--'}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Edited Duration</span>
                            {/* `--`, the same placeholder the neighbouring cells
                                use, while the stored speed curve is behind the
                                controls — a MotionPace length computed for the
                                previous max speed is simply the wrong number,
                                and a stat cell has no room to say so. The chart
                                directly above carries the explanation. */}
                            <span
                                className="stat-value"
                                data-testid="stat-edited-duration"
                                title={editedDurationPending ? MOTION_PACE_CHART.durationPending : undefined}
                            >
                                {editedDurationPending ? '--' : calculateEditedDuration(editedDurationSeconds)}
                            </span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Final FPS</span>
                            <span className="stat-value">{currentProject.video?.fps ? Math.round(currentProject.video.fps) : '--'}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Final Resolution</span>
                            <span className="stat-value">{(currentProject.video?.resolution?.width && currentProject.video?.resolution?.height) ? `${currentProject.video.resolution.width}x${currentProject.video.resolution.height}` : '--'}</span>
                        </div>
                    </div>
                </div>
            )}
            <div className="pv-center-button-container">
                {(() => {
                    const isGenerating = isRenderInFlight(currentProject.editedVideo);
                    const creditCost = estimateCreditCost(currentProject);
                    const balance = Number.isFinite(credits?.balance) ? credits.balance : null;
                    const cannotAfford = creditCost != null && balance != null && balance < creditCost;
                    // Once a render has reached a terminal state, that panel is the
                    // primary state below (finished video + Download for 'complete',
                    // the failure/cancelled panels in RenderStatusPanels.js); a
                    // low-credit shortfall must not dominate it and read like a
                    // failure — and for 'cancelled' the credits were just refunded,
                    // so the shortfall may be stale. Affordability for a re-render is
                    // enforced in the render dialog on actual attempt. (#363)
                    const renderStatus = currentProject.editedVideo?.status;
                    const isTerminalStatus = ['complete', 'failed', 'cancelled'].includes(renderStatus);
                    return (
                        <>
                            {blockReason && !isGenerating && (
                                <p className="cost-estimate cost-estimate--error" data-testid="render-blocked">
                                    {blockCopy(motionPaceEnabled)[blockReason]}
                                </p>
                            )}
                            {/* Pricing is by footage, not by feature (#341): this
                                number is derived from the source video's length
                                and resolution alone, so toggling operations above
                                never moves it. Keep it that way — a per-toggle
                                meter would put a price on the exploration these
                                cards exist to encourage. */}
                            {creditCost != null && !isGenerating && !cannotAfford && (
                                <p className="cost-estimate" data-testid="cost-estimate">
                                    This will use {creditCost} {creditCost === 1 ? 'credit' : 'credits'}
                                </p>
                            )}
                            {cannotAfford && !isGenerating && !isTerminalStatus && (() => {
                                const shortBy = creditCost - balance;
                                return (
                                    <div className="insufficient-credits-warning" data-testid="insufficient-credits-inline">
                                        <p className="insufficient-credits-warning__text">
                                            You're <strong>{shortBy} {shortBy === 1 ? 'credit' : 'credits'} short</strong> &mdash; this video needs {creditCost}, you have {balance}.
                                        </p>
                                        <Link to="/credits" className="insufficient-credits-warning__cta">
                                            Top up credits <span aria-hidden="true">&rarr;</span>
                                        </Link>
                                        {/* #350: this is the other pre-render price preview — it sits
                                            directly behind the render dialog's copy of the same
                                            warning — so it offers the same second remedy rather than
                                            top-up alone. Withheld when no tier grants more monthly
                                            credits than the account already has. */}
                                        {canUpgradeForCredits && (
                                            <button
                                                type="button"
                                                className="insufficient-credits-warning__cta insufficient-credits-warning__cta--plain"
                                                onClick={() => setShowCreditsUpsell(true)}
                                                data-testid="editor-credits-upsell-trigger"
                                            >
                                                Or get credits every month
                                            </button>
                                        )}
                                        {showCreditsUpsell && (
                                            <UpgradePromptDialog
                                                promptId={UPSELL_PROMPTS.CREDITS_EXHAUSTED}
                                                entitlements={entitlements}
                                                subscription={entitlements && entitlements.subscription}
                                                shortBy={shortBy}
                                                projectId={projectId}
                                                onTopUp={() => setShowCreditsUpsell(false)}
                                                onClose={() => setShowCreditsUpsell(false)}
                                            />
                                        )}
                                    </div>
                                );
                            })()}
                            <button onClick={onCreateVideo} disabled={isCreating || isRecalculating || isGenerating || !!blockReason} className="btn btn-teal">
                                {isRecalculating ? 'Recalculating...' : (isCreating || isGenerating) ? <div className="spinner"></div> : currentProject.editedVideo?.status === 'complete' ? 'Recreate Video' : 'Create Video'}
                            </button>
                        </>
                    );
                })()}
            </div>
        </div>
    );
};

export default EditorPanel;
